import React, { useState } from 'react';
import { Box, Typography, Grid, Button } from '@mui/material';
import { useChowaTheme } from '../config/chowaThemeContext';
import Card from '../config/ChowaCard';
import { getEngineDataCombined } from '../api_config';
import { getModelsBySeries, getSeriesColor } from '../model/UnitSeries';

// ダッシュボードコンポーネントをインポート
import TimeEngineHoursBarChart from './timeEngineHoursBarChart';
import AllEngineHoursBarChart from './50ALL_timeEngineHoursBarChart';

// 新しいカードコンポーネントをインポート
import ModelRegistrationCard from './ModelRegistrationCard';
import TodayOperatingCard from './TodayOperatingCard';

const ZVSeriesPage = () => {
  const { setSeriesColor, isDark } = useChowaTheme();
  const [selectedModel, setSelectedModel] = useState(null);
  const [engineData, setEngineData] = useState([]);
  const [loading, setLoading] = useState(false);

  const models = getModelsBySeries('ZV');
  const seriesColor = getSeriesColor('ZV');

  const fetchSeriesData = async (model) => {
    setLoading(true);
    try {
      const [hoursResponse, daysResponse] = await getEngineDataCombined(model);

      if (!hoursResponse.data?.data || !daysResponse.data?.data) {
        console.error('Invalid API response format');
        setEngineData([]);
        return;
      }

      // データの結合
      const formattedData = hoursResponse.data.data.map(item => {
        const daysData = daysResponse.data.data.deviceOperationDays.find(
          d => d.deviceId === item.deviceId
        );
        const isError = item.isCanError;
        
        let hours = 0;
        if (isError && item.validData) {
          hours = parseInt(item.validData, 10) || 0;
        } else if (item.latestData) {
          hours = parseInt(item.latestData, 10) || 0;
        }

        return {
          name: item.modelName,
          deviceId: item.deviceId,
          hours: hours,
          timestamp: isError ? item.validTimestamp : item.latestTimestamp,
          operationDays: daysData ? daysData.operationDays : 0,
          isCanError: isError,
          latestTimestamp: item.latestTimestamp,
          modelSeries: item.modelSeries,
          modelType: item.modelType,
          unitSerialNumber: item.unitSerialNumber
        };
      });

      setEngineData(formattedData);
    } catch (error) {
      console.error('データの取得に失敗しました:', error);
      setEngineData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleModelSelect = (model) => {
    setSelectedModel(model);
    setSeriesColor('ZV');
    // CustomEventでサイドバーに通知
    window.dispatchEvent(new CustomEvent('modelSelected', { 
      detail: { model } 
    }));
    // データ取得
    fetchSeriesData(model);
  };

  return (
    <Box sx={{ p: 4 }}>
      {/* モデル選択ボタン */}
      <Card style={{ padding: '24px', marginBottom: '32px', borderColor: seriesColor }}>
        <Typography variant="h6" sx={{ mb: 3, color: seriesColor }}>
          機種を選択してください
        </Typography>
        <Grid container spacing={2}>
          {models.map((model) => (
            <Grid item key={model}>
              <Button
                variant={selectedModel === model ? "contained" : "outlined"}
                onClick={() => handleModelSelect(model)}
                sx={{
                  backgroundColor: selectedModel === model ? seriesColor : 'transparent',
                  borderColor: seriesColor,
                  color: selectedModel === model ? 'white' : seriesColor,
                  '&:hover': {
                    backgroundColor: selectedModel === model ? 
                      `${isDark ? `${seriesColor}CC` : `${seriesColor}40`} !important` : 
                      `${isDark ? `${seriesColor}30` : `${seriesColor}25`} !important`,
                    borderColor: `${seriesColor} !important`
                  },
                  minWidth: 120,
                  height: 48
                }}
              >
                {model}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Card>

      {/* 統計カード */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <ModelRegistrationCard selectedModel={selectedModel} seriesColor={seriesColor} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TodayOperatingCard seriesKey="ZV" seriesColor={seriesColor} selectedModel={selectedModel} />
        </Grid>
      </Grid>

      {/* 選択されたモデルのダッシュボード */}
      {selectedModel && (
        <Box>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <Typography sx={{ ml: 2 }} color="text.secondary">
                データを読み込み中...
              </Typography>
            </Box>
          )}

          {!loading && engineData.length > 0 && (
            <>
              <TimeEngineHoursBarChart data={engineData} seriesColor={seriesColor} selectedModel={selectedModel} />
              <div style={{ marginTop: '32px' }}>
                <AllEngineHoursBarChart data={engineData} seriesColor={seriesColor} selectedModel={selectedModel} />
              </div>
            </>
          )}

          {!loading && selectedModel && engineData.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                {selectedModel} のデータが見つかりません
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ZVSeriesPage; 